import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import portal from "@vacancorp/portal";
// vue
import { mapState, mapMutations } from "vuex";
// tools
import initGMaps from "@/util/gmaps";
import EventBus from "@/event-bus";
import { getLocalStorage, setLocalStorage } from "@/util/utils";
import { parameterizedURLQuery, genreCategoryListStringToArray } from "@/mixins/common-functions";
// types
import { MapViewDataType, SplitViewRef } from "@/views/@types/maps-view";
type DataType = {
  vacancyTimer?: NodeJS.Timer;
  showEvacuationAlert: boolean;
  showEntryRestrictedPopup: { value: boolean };
};
// helper func
import { getGenerMapZoomFromParams, getPlaceIdHashList } from "@/mixins/common-functions";
import { setUnknownDisplayTextOnVacancy404, refillVacancyListOnContentError } from "@/usecase/vacancy";
import { FilterParams } from "@/api/adapter.api";
/** @remarks evacuation drill status */
import { ResponseGetEvacuationDrillMode } from "@/@types/evacuation-drill";
import { evacuationDrillStatusUsecase, hasDiffEvacuationDrillPlaces } from "@/usecase/evacuation-drill";
import { setCookie } from "@/util/cookie";
import { $_sortPlacesByDistanceOnMaps, $_sortPlacesByVacancyOnMaps } from "@/util/places-vacancy.helper";
// mixins
import MapsViewVacancyErrorMixin from "@/mixins/maps-view-vacancy-error";
import MapsMixin from "@/mixins/google-maps";
import VueTypedMixin from "vue-typed-mixins";
import { translateI18n } from "@/util/i18n.helper";
import { DEFAULT_LOCATION } from "@/models/locations.model";
import OpendataModuleMixin from "@/mixins/opendata-module.mixin";
import { PlaceService } from "@/api/services/place.service";

export default VueTypedMixin(MapsViewVacancyErrorMixin, MapsMixin, OpendataModuleMixin).extend({
  name: "mixin-map-view",
  mixins: [MapsViewVacancyErrorMixin, MapsMixin, OpendataModuleMixin],
  data(): DataType & MapViewDataType {
    return {
      vacancyTimer: undefined,
      showEvacuationAlert: false,
      showEntryRestrictedPopup: { value: false },
      // maps-views
      gMapsLoaded: undefined,
      isPlacesAlreadySorted: false,
      pinSelectedStatus: "byCardView",
      isGMapInAction: false,
      isSearchAgain: false,
      isFirstSorting: true,
      showPollAlert: false,
      showNotReadyAlert: false,
      adsPopupData: {
        shown: false,
        content: "",
      },
    };
  },
  watch: {
    // watchers in mixins will be trigger twice
  },
  computed: {
    // data-module
    showGPSDenyAlertByTimeout(): boolean {
      return this.$store.state.data.showGPSDenyAlertByTimeout;
    },
    placeList(): PortalAdapterApi.ResponseGetPublicPlace[] {
      return this.$store.state.data.placeList;
    },
    displayVacancyList(): PortalAdapterApi.ResponseGetMergedDisplayVacancy[] {
      return this.$store.state.data.displayVacancyList;
    },
    isFirstLoadMap(): boolean | undefined {
      return this.$store.state.data.isFirstLoadMap;
    },
    selectedGenre(): PortalAdapterApi.PlaceCategoryType | undefined {
      return this.$store.state.data.selectedGenre;
    },
    selectedPlaceIdHash(): string | undefined {
      return this.$store.state.data.selectedPlaceIdHash;
    },
    sortedPlaceList(): portal.PublicPlaceWithVacancyId[] {
      return this.$store.state.data.sortedPlaceList;
    },
    selectedTopPlace(): PortalAdapterApi.TopArea | undefined {
      return this.$store.state.data.selectedTopPlace;
    },
    // ui-module
    ...mapState("ui", ["showDenyGpsAlertView", "showLoadingCard", "isAreaViewModal"]),
    alertView(): { status: false; msg: ""; btnTitle: "" } {
      return this.$store.state.ui.alertView;
    },
    isMobile(): boolean {
      return this.$store.getters["ui/isMobile"];
    },
    // for GPS alert
    showAlertUI(): boolean {
      return this.showDenyGpsAlertView || this.showGPSDenyAlertByTimeout;
    },
    showAlertTitle(): string | undefined {
      if (this.showDenyGpsAlertView) {
        return this.$i18n.t("Alert.denyGpsTitle") as string;
      } else if (this.showGPSDenyAlertByTimeout) {
        return this.$i18n.t("Alert.timeoutGpsTitle") as string;
      } else {
        return undefined;
      }
    },
    showAlertSubtitle(): string | undefined {
      if (this.showDenyGpsAlertView) {
        return this.$i18n.t("Alert.denyGpsSubTitle") as string;
      } else if (this.showGPSDenyAlertByTimeout) {
        return this.$i18n.t("Alert.timeoutGpsSubTitle") as string;
      } else {
        return undefined;
      }
    },
    // network alert
    showAlertViewMsg(): string {
      return this.alertView.msg === "" ? (this.$i18n.t("Alert.networkError") as string) : this.alertView.msg;
    },
    showAlertViewBtnTitle(): string {
      return this.alertView.btnTitle === "" ? (this.$i18n.t("Alert.reloadBtn") as string) : this.alertView.btnTitle;
    },
    // polls alert
    showPollDisplayContentI18n(): PortalAdapterApi.I18nObject | undefined {
      if (!this.selectedTopPlace) return undefined;
      return this.selectedTopPlace.displayContentI18n !== undefined
        ? this.selectedTopPlace.displayContentI18n
        : undefined;
    },
    splitViewRef(): SplitViewRef {
      return this.$refs.splitView as SplitViewRef;
    },
  },
  created() {
    // async load google maps
    initGMaps()
      .then(() => {
        this.initMarkerIcons();
        this.gMapsLoaded = true;
      })
      .catch(() => {
        this.gMapsLoaded = false;
        console.error(`init google maps lib error`);
      });
    // watchers
    this.watchMapsLoaded();
    this.watchSelectedGenre();
    this.watchSelectedTopPlace();
    this.watchVacancyErrorAlertCounts();
    this.watchDisplayVacancyList();
    this.watchPlaceList();
    this.watchSortedPlaceList();
    this.initEventBusListeners();
  },
  async mounted() {
    // do not show no-shop-alert if it showed before and reload page
    this.$store.commit("ui/setAlertBar", {
      status: false,
      title: "",
      subtitle: "",
    });
  },
  beforeDestroy() {
    this.clearVacancyTimer();
  },
  methods: {
    // mutations
    ...mapMutations("data", ["setIsFirstSearch"]),
    ...mapMutations("ui", ["setShowLoadingCard", "setBottomSectionOffsetTop"]),
    setIsPlacesAlreadySorted(payload: boolean) {
      this.isPlacesAlreadySorted = payload;
    },
    setIsSearchAgain(payload: boolean) {
      this.isSearchAgain = payload;
    },
    setGMapsLoaded(payload: boolean) {
      this.gMapsLoaded = payload;
    },
    setIsFirstSorting(payload: boolean) {
      this.isFirstSorting = payload;
    },
    setPinSelectedStatus(payload: portal.PinSelectedStatus) {
      this.pinSelectedStatus = payload;
    },
    setIsGMapInAction(payload: boolean) {
      this.isGMapInAction = payload;
    },
    /** @remarks vacancy */
    setVacancyTimer(payload?: NodeJS.Timer) {
      this.vacancyTimer = payload;
    },
    evacuationAlertOkClicked() {
      this.showEvacuationAlert = false;
    },
    evacuationSelected() {
      this.showEvacuationAlert = true;
    },
    /**
     * @remarks entryRestricted
     * remember to show alert popup once
     * */
    checkIsEntryRestrictedShownBefore(): boolean {
      if (this.$route.path.includes("/area/enoshima")) {
        const isEntryRestrictedPopupStored: string | null = getLocalStorage("is-entry-restricted-popup-shown");
        return isEntryRestrictedPopupStored === null || isEntryRestrictedPopupStored === "false" ? false : true;
      } else {
        return true;
      }
    },

    shouldAdsAlertShown(areaName: string): boolean {
      // only accept /area/xxx and deny /map/latlng?areaName=xxx
      return this.$route.path.includes(`/area/${areaName}`);
    },

    closeEntryRestrictedPopup() {
      this.$set(this.showEntryRestrictedPopup, "value", false);
      setLocalStorage("is-entry-restricted-popup-shown", "true");
    },
    /** @remarks maps view methods */
    ...mapMutations("data", [
      "setIsFirstLoadMap",
      "setLocation",
      "setSelectedPlaceIdHash",
      "setSortedPlaceList",
      "setPlaceList",
      "setSelectedFilterRadioButtons",
      "setSelectedGenreCategoryList",
      "setShowGPSDenyAlertByTimeout",
      "setSelectedTopPlace",
    ]),
    ...mapMutations("ui", ["setShowTopAreaList", "setShowDenyGpsAlertView"]),
    gpsAlertOkClicked() {
      this.setShowDenyGpsAlertView(false);
      this.setShowGPSDenyAlertByTimeout(false);
    },
    // poll
    pollAlertOkClicked() {
      this.setShowPollAlert(false);
      if (this.$route.fullPath.includes("kobe-city-evacuation")) {
        this.setShowNotReadyAlert(true);
      }
    },
    setShowPollAlert(payload: boolean) {
      this.showPollAlert = payload;
    },
    pollSearchBtnClicked() {
      console.log(`----pc poll-search-clicked----`);
      const latlngs: PortalAdapterApi.Location[] = this.sortedPlaceList.map(
        (place: portal.PublicPlaceWithVacancyId) => {
          return { lat: place.location.lat, lng: place.location.lng };
        },
      );
      this.googleMapsRef?.boundMap(latlngs);
      // need to wait map stops moving
      setTimeout(() => {
        this.isSearchAgain = false;
      }, 1000);
    },
    // notReady 神戸市開設可能
    notReadyAlertOkClicked() {
      this.setShowNotReadyAlert(false);
    },

    setShowNotReadyAlert(payload: boolean) {
      this.showNotReadyAlert = payload;
    },

    setAdsAlert({ shown, content }: { shown: boolean; content: string }) {
      this.adsPopupData = {
        shown,
        content,
      };
    },

    hideAdsAlertClicked() {
      const areaName = this.selectedTopPlace?.areaName ?? "";
      setCookie("/vacan/maps/ads-popup", areaName, 1);
      this.adsPopupData.shown = false;
    },

    // instance filter buttons
    filterBtnClicked() {
      this.searchBtnClicked();
    },

    // Access by /area/area-name
    async searchByAccessAreaName(areaName: string) {
      const {
        genre,
        mapZoom,
        genreCategoryListStr,
      }: {
        genre?: string;
        mapZoom?: string;
        genreCategoryListStr?: string;
      } = getGenerMapZoomFromParams();
      console.log(`searchByAccessAreaName😀`);
      console.log(`genre: ${genre}`);
      // re-assign genre after route changed
      const newGenre = genre === undefined ? this.selectedGenre : genre;
      this.$store.commit("data/setSelectedGenre", newGenre);

      // re-assign genre category
      const newGenreCategoryList: undefined | string[] = genreCategoryListStringToArray(genreCategoryListStr);

      this.setSelectedGenreCategoryList(newGenreCategoryList);

      // get location in /area/areaName endpoint
      this.getLocationByAreaName(areaName, mapZoom);
    },

    searchByLatLngParam(latlng: string) {
      // timeout to getting latest route
      setTimeout(() => {
        // do not request on area view switching
        if (this.$route.fullPath.includes("#area") || this.$route.fullPath.includes("#prefecture")) {
          return;
        }
        const target: PortalAdapterApi.TopArea = this.getTargetLocationFromParam(latlng);
        const [latStr, lngStr, zoomStr]: string[] = latlng.split(",");
        console.log(`--- searchByLatLngParam ${latStr}, ${lngStr}, ${zoomStr}---`);
        // set map zoom
        this.$_setMapZoom(zoomStr ? Number(zoomStr) : this.mapsZoom);
        // Move maps by latlng from URL
        this.googleMapsRef?.moveMapTo(target.location);
        // Check if coming with URL params
        const query = this.getFilterParamsFromUrlQuery();
        this.setFilterParams(query);

        // check place list before search again
        console.warn(`--- resetPlacesData --`);
        this.clearVacancyTimer();
        this.resetPlacesData();
        const { source } = this.$route.query;
        if (source !== undefined && this.sortedPlaceList.length > 0) {
          const tmp = [...this.sortedPlaceList];
          setTimeout(() => {
            this.setPlaceList(tmp);
          }, 100);
        } else {
          this.getPlaceList(target.location as PortalAdapterApi.Location);
        }
      }, 100);
    },
    getPlaceListFromTopPlace() {
      this.setShowLoadingCard(true);
      if (this.selectedTopPlace === undefined) {
        if (this.$store.state?.data?.location?.lat === undefined) {
          this.setShowLoadingCard(false);
          return;
        }
        this.getPlaceList(this.$store.state?.data?.location);
      } else {
        this.getPlaceList(this.selectedTopPlace.location);
      }
    },

    // Direct URL access
    // Allow /area/areaName only
    getLocationByAreaName(areaName: string | undefined, mapZoom: string | undefined) {
      console.log(`getLocationByAreaName😀`);
      if (areaName === undefined) return;
      let newMapZoom: number = mapZoom === undefined ? 13 : Number(mapZoom);
      this.setShowLoadingCard(true);

      // area non-selected
      console.warn(`------- areaName = ${areaName} -----------`);
      console.log(`newMapZoom = ${newMapZoom}`);
      if (areaName === "Default" || areaName === "current-place" || areaName === "") {
        this.searchBtnClicked();
        return;
      }
      const isOpendata = this.$route.query.isOpendata === "true";
      this.setIsOpendataMixin(isOpendata);
      this.$store
        .dispatch("data/getLocationByAreaName", {
          areaName,
          mapsMapType: this.selectedGenre,
          isOpendata,
        })
        .then(() => {
          // poll announcements from utility
          if (this.selectedTopPlace?.displayContentI18n !== undefined) {
            if (
              this.selectedTopPlace?.areaName === "sano-city-sanoramen" ||
              this.selectedTopPlace?.areaName === "odaiba-area-dic-project"
            ) {
              this.setAdsAlert({
                shown: true,
                content: translateI18n(this.selectedTopPlace?.displayContentI18n),
              });
            } else {
              this.setShowPollAlert(true);
            }
          }
          /**
           * @remarks
           * after poll checks
           * show everytime on accessing kobe - city - evacuation directly on first load
           */
          if (this.$route.fullPath.includes("kobe-city-evacuation") && !this.showPollAlert) {
            this.setShowNotReadyAlert(true);
          }
          // handle undefined zoom
          newMapZoom =
            mapZoom === undefined
              ? this.selectedTopPlace?.zoomLevel === undefined
                ? 13
                : this.selectedTopPlace?.zoomLevel
              : newMapZoom;
          console.log(`get by area name with zoom: ${newMapZoom}`);
          this.$_setMapZoom(newMapZoom);
          this.googleMapsRef?.setMapCenter(this.selectedTopPlace?.location ?? DEFAULT_LOCATION);
          this.isSearchAgain = false;
        })
        .catch(() => {
          this.setShowLoadingCard(false);
          if (this.isMobile) this.splitViewRef.moveCardViewUp();
        });
    },

    checkLoginStatus() {
      const accessToken: string | null = getLocalStorage("session/tokens/accessToken");
      return accessToken === null || accessToken.length === 0 ? false : true;
    },

    clearVacancyTimer() {
      this.killVacancyTimer();
    },
    killVacancyTimer() {
      console.log(`--- killVacancyTimer ---`);
      window.clearInterval(this.vacancyTimer as NodeJS.Timer);
      this.setVacancyTimer(undefined);
    },
    /** @remarks 再検索ボタン */
    searchBtnClicked() {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Map item",
        event_label: "Search button",
        value: 1,
      });
      this.isSearchAgain = false;
      this.isFirstSorting = true;
      this.setShowLoadingCard(true);
      // vacan places & opendata placesを両立して取られるため,
      // isOpendata & selectedTopPlaceをリセット
      this.setIsOpendataMixin(false);
      const location: PortalAdapterApi.Location = this.getMapCenterLocation();
      this.setSelectedTopPlace({
        areaName: "",
        displayText: this.$i18n.t("Nav.area").toString(),
        location: location,
        displayOrder: 0,
      });
      this.clearVacancyTimer();
      this.resetPlacesData();
      this.goToMapsRoute(location, true);
    },
    pollSearchClicked() {
      console.log(`----poll-search-clicked----`);
      const latlngs: PortalAdapterApi.Location[] = this.sortedPlaceList.map(
        (place: portal.PublicPlaceWithVacancyId) => {
          return { lat: place.location.lat, lng: place.location.lng };
        },
      );
      this.googleMapsRef?.boundMap(latlngs);
      // need to wait map stops moving
      setTimeout(() => {
        this.isSearchAgain = false;
      }, 1000);
    },
    // output to /map URL
    goToMapsRoute(location: PortalAdapterApi.Location, removeSource?: boolean) {
      console.log(`--- goToMapsRoute ---`);
      this.setIsFirstSearch(false); // filters and genre
      let params: any = this.getFilterParams();
      params = {
        ...this.$route.query,
        ...params,
        source: removeSource ? undefined : params.source,
        areaName: this.selectedTopPlace?.areaName ?? undefined,
      };

      console.log(`😀search by place: ${JSON.stringify(params)}`);
      this.$router
        .push(`/map/${location.lat},${location.lng},${this.mapsZoom}?${parameterizedURLQuery(params)}`)
        .catch(() => {
          console.warn("it's the same route path, router push caught error on goToMapsRoute, try 2nd time");
          this.$router
            .push(`/map/${location.lat},${location.lng + 0.000001},${this.mapsZoom}?${parameterizedURLQuery(params)}`)
            .catch(() => console.warn("router push 2nd error, it stops"));
        });
    },
    getFilterParamsFromUrlQuery() {
      const {
        vacant,
        favorite,
        genre,
        genreCategoryList,
      }: {
        vacant?: { [Key: string]: string };
        favorite?: { [Key: string]: string };
        genre?: { [Key: string]: string };
        genreCategoryList?: any;
      } = this.$route.query;

      const query: any = {
        vacant,
        favorite,
        genre,
        genreCategoryList: genreCategoryListStringToArray(genreCategoryList),
      };
      return query;
    },
    resetLayerOfWhiteBgView() {
      this.$store.commit("ui/setShowFacility", false);
      this.$store.commit("ui/setAnnouncementViewShown", false);
      this.$store.commit("ui/setZIndex", 1);
    },
    resetPlacesData() {
      this.isPlacesAlreadySorted = false;
      this.isSearchAgain = false;
      this.setPlaceList([]);
      this.setSortedPlaceList([]);
      this.clearMarkers();
      this.setSelectedPlaceIdHash("");
      this.splitViewRef.currentCardIndex = 0;
      this.isFirstSorting = true;
    },
    /** @remarks get place data */
    getPlaceList(location: PortalAdapterApi.Location | undefined) {
      this.setShowLoadingCard(true);
      this.resetVacancyErrorCounting();
      if (location === undefined || location.lat === undefined) {
        this.setShowLoadingCard(false);
        return;
      }
      this.setIsFirstLoadMap(false);
      this.$store.commit("ui/setAlertBar", {
        status: false,
        title: "",
        subtitle: "",
      });

      const countryName: string | undefined = this.$store.getters["data/getSelectedCountry"];
      const areaName: string | undefined = this.$store.state.data.selectedTopPlace?.areaName;

      // all filters
      const params: FilterParams = this.getFilterParams();
      const mapType = params.genre === "disaster-prevention" ? "water-gate" : undefined;

      this.$store
        .dispatch("data/fetchPlace", {
          lat: location.lat,
          lng: location.lng,
          radius: this.googleMapsRef?.radiusFromCenter(),
          countryName,
          params,
          areaName,
          mapType,
          isOpendata: this.isOpendataMixin,
        })
        .then((res: PortalAdapterApi.ResponseGetPublicPlace[]) => {
          if (this.isMobile) this.splitViewRef.moveCardViewUp();
          if (res.length === 0) {
            // for animation
            setTimeout(() => {
              this.$store.commit("ui/setAlertBar", {
                status: true,
                title: this.$i18n.t("Alert.alertBarNoShop"),
                subtitle: this.$i18n.t("Alert.alertBarNoShop2"),
              });
              this.isSearchAgain = false;
              this.setShowLoadingCard(false);
              this.setBottomSectionOffsetTop(undefined);
            }, 1000);
          } else {
            // clear places
            this.resetPlacesData();
            this.$store.commit("data/setPlaceList", res);
            this.$store.commit("ui/isFetchingError", false);

            const genre: PortalAdapterApi.PlaceCategoryType | undefined = this.selectedGenre;
            if (genre !== undefined && genre[0] === "evacuation-center") {
              this.evacuationSelected();
            }
          }
        })
        .catch(() => {
          // for animation
          setTimeout(() => {
            this.isSearchAgain = false;
            this.setShowLoadingCard(false);
          }, 1000);
          if (this.isMobile) this.splitViewRef.moveCardViewUp();
          this.$store.commit("ui/isFetchingError", true);
          this.$store.commit("ui/setAlertView", {
            status: true,
            msg: this.$i18n.t("Alert.networkError"),
            btnTitle: this.$i18n.t("Alert.reloadBtn"),
          });
        });
    },
    setupVacancyTimer(placeIdHashList: string[]) {
      if (this.vacancyTimer !== undefined) return;
      // opendata has no vacancy status
      if (this.isOpendataEvacuationCenterMixin) return;
      console.log(`--- setupVacancyTimer ---`);
      const checkDuration: number = 10000; // 10 sec
      const timer: NodeJS.Timer = setInterval(async () => {
        // REVIEW: take off previous killTimer condition for 404, 500 endpoint errors
        let list = await PlaceService.fetchFacilityVacancyListByPlaceIdHashList(placeIdHashList).catch(() => {
          console.error(`caught facility list endpoint error 404, 500`);
        });
        // 404 500 endpoint errors
        if (!list) {
          list = setUnknownDisplayTextOnVacancy404(this.sortedPlaceList);
        } else {
          list = refillVacancyListOnContentError(list, this.sortedPlaceList);
        }
        this.$store.commit("data/setFacilityVacancyList", list);
      }, checkDuration);
      this.setVacancyTimer(timer);
    },
    getPlaceFacilityVacancyList() {
      const placeIdHashList: string[] = getPlaceIdHashList(this.placeList);
      this.setupVacancyTimer(placeIdHashList);
      // used for timer and all pages
      this.$store.commit("data/setSelectedPlaceIdHashList", placeIdHashList);
      this.$store
        .dispatch("data/getPlaceFacilityVacancyList", placeIdHashList)
        .then((res: PortalAdapterApi.ResponseGetMergedDisplayVacancy[]) => {
          const refilledList: PortalAdapterApi.ResponseGetMergedDisplayVacancy[] = refillVacancyListOnContentError(
            res,
            this.sortedPlaceList,
          );
          this.$store.commit("data/setFacilityVacancyList", refilledList);
        })
        .catch(() => {
          // all error e.g. 404, 500
          console.error(`get facility list error`);
          this.addVacancyErrorAlertCounts();
          this.setVacancyErrorBannerShown(true);
          // 404 500 endpoint errors
          const list = setUnknownDisplayTextOnVacancy404(this.sortedPlaceList);
          this.$store.commit("data/setFacilityVacancyList", list);
        });
    },
    /** @remarks 大切 sorted place list initial 初始化 */
    initSortedPlaceList(placeList: PortalAdapterApi.ResponseGetPublicPlace[]) {
      const sortedPlaceList: portal.PublicPlaceWithVacancyId[] = placeList.map((place) => {
        return {
          ...place,
          statusOrder: 999,
        } as portal.PublicPlaceWithVacancyId;
      });
      this.$store.commit("data/setSortedPlaceList", sortedPlaceList);
    },
    /** @remarks deep watchers */
    // copy to sorted placeList and get vacancy
    watchPlaceList() {
      this.$watch(
        () => {
          return this.$store.state.data.placeList;
        },
        (newVal: PortalAdapterApi.ResponseGetPublicPlace[]) => {
          if (newVal === undefined || newVal.length === 0) return;
          this.initSortedPlaceList(newVal);
          this.getPlaceFacilityVacancyList();
        },
        {
          deep: true,
        },
      );
    },
    watchSortedPlaceList() {
      this.$watch(
        () => {
          return this.sortedPlaceList;
        },
        (newVal: portal.PublicPlaceWithVacancyId[]) => {
          if (newVal.length > 0) {
            if (newVal[0].statusOrder === 999) return;
            this.addMarkers(newVal);
            this.styleMarkers(this.displayVacancyList);
            if (this.selectedPlaceIdHash === undefined || this.selectedPlaceIdHash.length === 0) {
              const placeIdHash = newVal[0].placeIdHash;
              this.isSearchAgain = false;

              // back from detail page
              const { source }: { source?: string } = this.$route.query;
              if (source) {
                this.setSelectedPlaceIdHash(source);
              } else {
                this.setSelectedPlaceIdHash(placeIdHash);
              }
              console.log(`first sorting: ${this.isFirstSorting}`);

              if (this.isFirstSorting) {
                this.isSearchAgain = false;
                // back from detail page
                if (source) {
                  console.log(`isMobile: ${this.isMobile}`);
                  console.log(`moveToSelectedCard: ${source}`);
                  this.moveToSelectedCard(source);
                  if (!this.isMobile) {
                    this.$nextTick(() => {
                      this.googleMapsRef.moveMapsToSelectedMarker(source);
                    });
                  }
                } else {
                  this.moveToSelectedCard(placeIdHash);
                }
              }
            }
            // 1. move map & card
            // 2. enlarge marker
            this.enlargeMarkerUI();
            this.isFirstSorting = false;
          } else {
            this.isSearchAgain = false;
          }
        },
        {
          deep: true,
        },
      );
    },
    watchCurrentLocation() {
      this.$watch(
        () => {
          return this.$store.state.data.location;
        },
        (newVal: PortalAdapterApi.Location) => {
          this.setLocation(newVal);
          if (!this.moveCurrentLocation) return;
          this.moveCurrentLocation.location = newVal;
          this.googleMapsRef?.moveMapToCurrentLocation(this.moveCurrentLocation);
        },
        {
          deep: true,
        },
      );
    },
    checkIfPlaceHasSameBuilding(index: number) {
      // record same building state
      const place: undefined | portal.PublicPlaceWithVacancyId = this.$store.state.data.sortedPlaceList[index];
      if (place === undefined) return;
      const hasSameBuilding: boolean = place?.building === undefined ? false : true;
      this.$store.commit("ui/setSelectedCardHasSameBuilding", hasSameBuilding);
    },
    moveToSelectedPinAndCard(placeIdHash: string) {
      if (this.isFirstSorting) {
        // prevent ref dom is called before mounted
        this.$nextTick(() => {
          this.googleMapsRef?.moveMapsToSelectedMarker(placeIdHash);
        });
      }
      this.enlargeMarkerUI();
      this.isSearchAgain = false;
      if (this.pinSelectedStatus === "byCardView") return;
      this.moveToSelectedCard(placeIdHash);
    },
    moveToSelectedCard(placeIdHash: string) {
      const toIndex: number = this.findIndexToMovePlacePin(placeIdHash, this.sortedPlaceList);
      console.log(`moveToSelectedCard: update ref ${toIndex}`);
      if (toIndex === -1) return;
      this.$nextTick(() => {
        this.splitViewRef.currentCardIndex = toIndex;
      });
    },
    initEventBusListeners() {
      // update favorite
      EventBus.$on("favorite-checked", (placeIdHash: string) => {
        /** @remarks update favorite from the place */
        EventBus.$emit("update-favorite-to-places", {
          placeIdHash,
          status: true,
        });
      });
      // genre category
      EventBus.$on("place-category-selected", () => {
        this.resetPlacesData();
        this.searchBtnClicked();
        this.$store.commit("ui/setAlertBar", {
          status: false,
          title: "",
          subtitle: "",
        });
      });
      /**@remarks functions triggered in google-maps.vue & place-info-navigation.vue */
      EventBus.$on("toggle-pin-selected-status", (payload: portal.PinSelectedStatus) => {
        this.pinSelectedStatus = payload;
      });
      EventBus.$on("change-gMap-in-action", (payload: boolean) => {
        this.isGMapInAction = payload;
        this.$store.commit("ui/setAlertBar", {
          status: false,
          title: "",
          subtitle: "",
        });
      });
      EventBus.$on("search-again", (payload: boolean) => {
        this.isSearchAgain = payload;
      });
      EventBus.$on("move-maps-selected-marker", () => {
        // TODO: sprint 11/17
        // it will be merged and refactor here
        // because the UI will be changed again
        const placeIdHash: string = this.selectedPlaceIdHash ?? "";
        // prevent ref dom is called before mounted
        this.$nextTick(() => {
          this.googleMapsRef?.moveMapsToSelectedMarker(placeIdHash);
        });
        this.isSearchAgain = false;
      });
      EventBus.$on("move-maps-card-to-selected-marker", () => {
        const placeIdHash: string = this.selectedPlaceIdHash ?? "";
        this.moveToSelectedPinAndCard(placeIdHash);
      });
      EventBus.$on("enlarge-selected-marker2", () => {
        this.enlargeMarkerUI();
      });
      EventBus.$on("check-if-place-has-same-building", (index: number) => {
        this.checkIfPlaceHasSameBuilding(index);
      });
    },
    // watchers
    /**
     * @remarks reset selected filters on map type switched
     * normal - evacuation-center - polling-place
     * */
    watchSelectedGenre() {
      this.$watch(
        () => this.selectedGenre,
        (newVal?: string, oldVal?: string) => {
          if (!newVal || oldVal) return;
          if (newVal === oldVal) return;
          if (
            newVal === "evacuation-center" ||
            newVal === "polling-place" ||
            oldVal === "evacuation-center" ||
            oldVal === "polling-place"
          ) {
            this.setSelectedFilterRadioButtons([]);
            this.setSelectedGenreCategoryList([]);
          }
        },
      );
    },
    watchSelectedTopPlace() {
      // getLocationByAreaName() -> save topPlace -> goToMapsRoute() -> search places
      this.$watch(
        () => this.selectedTopPlace,
        (newVal: PortalAdapterApi.TopArea | undefined | null, oldVal: PortalAdapterApi.TopArea | undefined | null) => {
          if (newVal === undefined || newVal === null || newVal.location === undefined) {
            this.$store.commit("ui/setAlertView", {
              status: true,
              msg: this.$i18n.t("Alert.networkError"),
              btnTitle: this.$i18n.t("Alert.reloadBtn"),
            });
            return;
          }
          console.log(`watch selectedTopPlace: ${JSON.stringify(newVal)}`);
          // real device updates current-place in a loop so need to eairly return here
          if (oldVal?.areaName === "current-place" && newVal.areaName === "current-place") {
            console.warn(`old place and new place are both current-place`);
            return;
          }
          this.setShowLoadingCard(true);
          if (newVal.zoomLevel !== undefined)
            // if (!this.isAreaViewModal) {
            this.$_setMapZoom(Number(newVal.zoomLevel));
          else this.$_setMapZoom(13);

          setTimeout(() => {
            this.googleMapsRef?.setMapCenter(newVal.location);
            this.setIsSearchAgain(false);
            this.clearVacancyTimer();
            this.resetPlacesData();
            this.goToMapsRoute(newVal.location);
          }, 100);
        },
        {
          deep: true,
        },
      );
    },
    watchVacancyErrorAlertCounts() {
      this.$watch(
        () => this.vacancyErrorAlertCounts,
        (newNumber: number) => {
          if (newNumber >= 3 && !this.vacancyError3Times) {
            this.setVacancyErrorAlertShown(true);
            this.setVacancyError3Times(true);
          }
        },
      );
    },
    watchDisplayVacancyList() {
      this.$watch(
        () => this.displayVacancyList,
        (newVal: PortalAdapterApi.ResponseGetMergedDisplayVacancy[]) => {
          if (newVal === undefined || newVal.length === 0) return;
          // set color and text by vacancy status
          this.styleMarkers(newVal);
          // check vacancy error to show banner
          const hasVacancyError: boolean = this.hasVacancyError(newVal, this.sortedPlaceList);
          if (hasVacancyError) {
            this.addVacancyErrorAlertCounts();
            this.setVacancyErrorBannerShown(true);
          } else {
            // 連続３回 errorだから
            this.zeroVacancyErrorAlertCounts();
            this.setVacancyErrorBannerShown(false);
          }

          // sort places only on first access
          if (!this.isPlacesAlreadySorted) {
            $_sortPlacesByVacancyOnMaps(newVal);
            $_sortPlacesByDistanceOnMaps();
            // hide after finishing sorting
            this.setShowLoadingCard(false);
            this.setIsPlacesAlreadySorted(true);
          }

          // realtime evacuation drill status
          this.$nextTick(async () => {
            // get status
            const list: ResponseGetEvacuationDrillMode[] | undefined = await evacuationDrillStatusUsecase(
              this.sortedPlaceList.filter((place) => !place.isOpendata),
            ).catch(() => undefined);

            // only update on diff status list
            const diffEvacuationDrillPlaces: boolean = hasDiffEvacuationDrillPlaces<portal.PublicPlaceWithVacancyId>(
              list,
              this.sortedPlaceList,
            );

            if (list === undefined || !diffEvacuationDrillPlaces) return;
            // updatea UI
            list.forEach((drill) => {
              const targetPlace: portal.PublicPlaceWithVacancyId | undefined = this.sortedPlaceList.find(
                (p: portal.PublicPlaceWithVacancyId) => p.placeIdHash === drill.placeIdHash,
              );
              const targetIndex: number = this.sortedPlaceList.findIndex(
                (p: portal.PublicPlaceWithVacancyId) => p.placeIdHash === drill.placeIdHash,
              );
              if (targetPlace === undefined || targetIndex === -1) return;
              const targetNewPlace = {
                ...targetPlace,
                isInEvacuationDrill: drill.isInEvacuationDrill,
              };
              this.$store.commit("data/setSortedPlaceListIndex", {
                index: targetIndex,
                place: targetNewPlace,
              });
            });
          });
        },
      );
    },
    watchMapsLoaded() {
      this.$watch(
        () => this.gMapsLoaded,
        (newValue?: boolean) => {
          if (!newValue) return;
          if (this.hasCurrentLocation) {
            this.assignCurrentLocationIcon();
          }
          // open filter page directly
          const { areaMenu } = this.$route.query;
          if (areaMenu === "1") {
            this.setIsFirstLoadMap(false);
            setTimeout(() => {
              this.setShowTopAreaList(true);
            }, 100);
          }
          // clear persist-vuex
          this.$store.commit("ui/setShowFacility", false);
          this.$store.commit("ui/setViewState", "map");

          // watcher
          this.watchCurrentLocation();

          // first access or reload URL
          if (this.$route.path.includes("/map") && this.$route.params.latlng) {
            this.setIsFirstLoadMap(false);

            const { areaName }: { areaName?: string } = this.$route.query;
            if (areaName) {
              // Check if coming with URL params
              const query = this.getFilterParamsFromUrlQuery();
              this.setFilterParams(query);
              // to prevent vue router got previous parameters
              setTimeout(() => {
                this.searchByAccessAreaName(areaName);
              }, 100);
            } else {
              this.searchByLatLngParam(this.$route.params.latlng);
            }
          }

          if (this.$route.path.includes("/area") && this.$route.params.areaName) {
            this.setIsFirstLoadMap(false);

            // to prevent vue router got previous parameters
            setTimeout(() => {
              this.searchByAccessAreaName(this.$route.params.areaName);
            }, 100);
          }

          if (!this.checkIsEntryRestrictedShownBefore()) {
            this.$set(this.showEntryRestrictedPopup, "value", true);
          }

          // for first access /
          if (this.isFirstLoadMap === undefined) this.setIsFirstLoadMap(true);
        },
      );
    },
  },
});
